/**
 * AuthPage — страница входа, регистрации и восстановления пароля.
 * Отдельный роут /login для профессиональной структуры приложения.
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { t } from "../../i18n";

const API_URL = process.env.REACT_APP_API_URL || "https://b.supchats.com";

function pushDataLayerEvent(eventName, extra = {}) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...extra });
  } catch (e) {
    console.error(`Failed to push ${eventName} event:`, e);
  }
}

/** Возвращает URL веб-почты по домену email, или null для mailto fallback */
function getWebmailUrl(email) {
  if (!email || typeof email !== "string") return null;
  const addr = email.trim().toLowerCase();
  const at = addr.indexOf("@");
  if (at < 0) return null;
  const domain = addr.slice(at + 1);
  const domainMap = {
    "gmail.com": "https://mail.google.com/",
    "googlemail.com": "https://mail.google.com/",
    "yandex.ru": "https://mail.yandex.ru/",
    "yandex.com": "https://mail.yandex.ru/",
    "ya.ru": "https://mail.yandex.ru/",
    "yandex.by": "https://mail.yandex.by/",
    "yandex.kz": "https://mail.yandex.kz/",
    "yandex.ua": "https://mail.yandex.ua/",
    "outlook.com": "https://outlook.live.com/",
    "hotmail.com": "https://outlook.live.com/",
    "hotmail.ru": "https://outlook.live.com/",
    "live.com": "https://outlook.live.com/",
    "mail.ru": "https://e.mail.ru/",
    "inbox.ru": "https://e.mail.ru/",
    "bk.ru": "https://e.mail.ru/",
    "list.ru": "https://e.mail.ru/",
    "rambler.ru": "https://mail.rambler.ru/",
    "yahoo.com": "https://mail.yahoo.com/",
    "icloud.com": "https://www.icloud.com/mail",
    "me.com": "https://www.icloud.com/mail",
    "mac.com": "https://www.icloud.com/mail",
    "protonmail.com": "https://mail.proton.me/",
    "proton.me": "https://mail.proton.me/",
  };
  return domainMap[domain] || null;
}

function checkPasswordStrength(password) {
  const requirements = {
    minLength: {
      test: password.length >= 8,
      message: "Minimum 8 characters",
      met: password.length >= 8,
    },
    hasUpperCase: {
      test: /[A-Z]/.test(password),
      message: "Uppercase letter (A-Z)",
      met: /[A-Z]/.test(password),
    },
    hasLowerCase: {
      test: /[a-z]/.test(password),
      message: "Lowercase letter (a-z)",
      met: /[a-z]/.test(password),
    },
    hasNumbers: {
      test: /\d/.test(password),
      message: "Number (0-9)",
      met: /\d/.test(password),
    },
  };

  const score = Object.values(requirements).filter((req) => req.test).length;
  const isValid = score === 4;

  let message = "";
  if (score < 2) message = "Weak password";
  else if (score < 4) message = "Medium password";
  else message = "Strong password";

  return { score, message, isValid, requirements };
}

export default function AuthPage({ setToken }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const modeFromUrl = searchParams.get("mode") === "signup";

  const [isSignUp, setIsSignUp] = useState(() => {
    try {
      return localStorage.getItem("auth_mode") === "signup" || modeFromUrl;
    } catch (e) {
      return modeFromUrl;
    }
  });
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [contactDetails, setContactDetails] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, message: "" });
  const [resetPasswordStrength, setResetPasswordStrength] = useState({ score: 0, message: "" });
  const [showPostRegisterModal, setShowPostRegisterModal] = useState(false);
  const [captchaImage, setCaptchaImage] = useState(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaAnswer, setCaptchaAnswer] = useState("");

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const googleButtonContainerRef = useRef(null);
  const googleInitializedRef = useRef(false);
  const shouldShowGoogleButton = Boolean(googleClientId) && !isPasswordReset && !isForgotPassword;

  const finalizeLogin = useCallback(
    (tokenValue, remember = false, successMessage) => {
      setToken(tokenValue);

      if (remember) {
        localStorage.setItem("token", tokenValue);
        localStorage.setItem("rememberMe", "true");
        localStorage.setItem("tokenExpiry", Date.now() + 30 * 24 * 60 * 60 * 1000);
        sessionStorage.removeItem("token");
        localStorage.removeItem("sessionOnly");
      } else {
        localStorage.setItem("token", tokenValue);
        localStorage.setItem("sessionOnly", "true");
        sessionStorage.setItem("token", tokenValue);
        localStorage.removeItem("rememberMe");
        localStorage.removeItem("tokenExpiry");
      }

      setMessage(successMessage || t("Logged in successfully"));

      try {
        localStorage.removeItem("forceAuth");
      } catch (e) {}

      navigate("/agents");
    },
    [navigate, setToken]
  );

  const handleGoogleCredentialResponse = useCallback(
    async (response) => {
      const credential = response?.credential;
      if (!credential) return;

      setIsLoading(true);
      setMessage("");

      try {
        const apiResponse = await axios.post(
          `${API_URL}/auth/google`,
          { credential },
          { withCredentials: true }
        );

        const token = apiResponse.data?.access_token;
        if (apiResponse.data?.email_verified_event) {
          pushDataLayerEvent("email_verified");
        }
        if (token) {
          finalizeLogin(token, rememberMe);
        } else {
          setMessage(t("Google authentication failed. Please try again."));
        }
      } catch (error) {
        console.error("Google auth error:", error);
        const detail = error.response?.data?.detail;
        setMessage(detail || t("Google authentication failed. Please try again."));
      } finally {
        setIsLoading(false);
      }
    },
    [finalizeLogin, rememberMe]
  );

  useEffect(() => {
    if (!shouldShowGoogleButton || !googleClientId || typeof window === "undefined") {
      googleInitializedRef.current = false;
      return;
    }

    const initializeGoogle = () => {
      if (
        !window.google?.accounts ||
        !googleButtonContainerRef.current ||
        googleInitializedRef.current
      ) {
        return;
      }

      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleGoogleCredentialResponse,
      });

      window.google.accounts.id.renderButton(googleButtonContainerRef.current, {
        theme: "outline",
        size: "large",
        width: 260,
        type: "standard",
        shape: "rectangular",
        text: "signin_with",
      });

      googleInitializedRef.current = true;
    };

    if (window.google?.accounts) {
      initializeGoogle();
      return;
    }

    const existingScript = document.getElementById("google-gsi-script");
    if (existingScript) {
      const handleLoad = () => {
        existingScript.setAttribute("data-loaded", "true");
        initializeGoogle();
      };

      if (existingScript.getAttribute("data-loaded") === "true") {
        initializeGoogle();
      } else {
        existingScript.addEventListener("load", handleLoad);
        return () => existingScript.removeEventListener("load", handleLoad);
      }
      return;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.id = "google-gsi-script";
    script.onload = () => {
      script.setAttribute("data-loaded", "true");
      initializeGoogle();
    };
    document.head.appendChild(script);

    return () => {
      script.onload = null;
    };
  }, [googleClientId, handleGoogleCredentialResponse, shouldShowGoogleButton]);

  const handleSignIn = async () => {
    setIsLoading(true);
    setMessage("");

    try {
      const formData = new URLSearchParams();
      formData.append("username", email);
      formData.append("password", password);

      const response = await axios({
        method: "post",
        url: `${API_URL}/token`,
        data: formData.toString(),
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        withCredentials: true,
      });

      const token = response.data.access_token;
      finalizeLogin(token, rememberMe);
    } catch (error) {
      let errorMessage = t("Login error. Please check your username and password.");

      if (error.response) {
        if (error.response.status === 403) {
          errorMessage =
            error.response.data?.detail ||
            t("Your account has been suspended. Please contact administrator.");
        } else if (error.response.status === 401) {
          errorMessage = error.response.data?.detail || t("Invalid email or password");
        } else {
          errorMessage = error.response.data?.detail || t("Authentication error");
        }
      } else if (error.request) {
        errorMessage = t("No response from server. Please check your connection.");
      }

      setMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    if (isSignUp) {
      setPasswordStrength(checkPasswordStrength(newPassword));
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (isPasswordReset) handlePasswordReset();
    else if (isSignUp) handleSignUp();
    else if (isForgotPassword) handleForgotPassword();
    else handleSignIn();
  };

  const handleSignUp = async () => {
    if (password !== repeatPassword) {
      setMessage(t("Passwords do not match"));
      return;
    }
    if (!contactDetails.trim()) {
      setMessage(t("Contact Information field is required"));
      return;
    }
    if (!acceptTerms) {
      setMessage(t("You must accept the Terms of Service"));
      return;
    }
    if (!passwordStrength.isValid) {
      setMessage(t("Password does not meet security requirements"));
      return;
    }

    setIsLoading(true);
    try {
      let registrationTemplate = searchParams.get("template") || null;
      if (!registrationTemplate) {
        try {
          registrationTemplate = localStorage.getItem("template_interest") || null;
        } catch (e) {}
      }
      const payload = {
        email,
        password,
        contact_details: contactDetails,
        captcha_token: captchaToken,
        captcha_answer: captchaAnswer,
      };
      if (registrationTemplate) {
        payload.registration_template = registrationTemplate;
      }
      const response = await axios.post(`${API_URL}/register`, payload);

      setMessage("");
      setShowPostRegisterModal(true);
      if (response.data.email_sent) {
        try {
          localStorage.removeItem("template_interest");
        } catch (e) {}
        setIsSignUp(false);
        setPassword("");
        setRepeatPassword("");
        setContactDetails("");
        setAcceptTerms(false);
        setPasswordStrength({ score: 0, message: "" });
      }
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      const detailLower = typeof detail === "string" ? detail.toLowerCase() : "";
      if (typeof detail === "string" && /already\s*registered|exists/i.test(detail)) {
        setMessage(t("This email is already registered"));
      } else if (
        (status === 400 && detailLower.includes("captcha")) ||
        (typeof detail === "string" && /captcha/i.test(detail))
      ) {
        setMessage(t("Captcha validation failed. Please try again."));
        await refreshCaptcha();
      } else if (detail) {
        setMessage(detail);
      } else {
        setMessage(t("Registration failed"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const closePostRegisterModal = () => {
    setShowPostRegisterModal(false);
    setIsSignUp(false);
  };

  const refreshCaptcha = async () => {
    try {
      const res = await axios.get(`${API_URL}/captcha/new`);
      setCaptchaImage(res.data.image);
      setCaptchaToken(res.data.token);
      setCaptchaAnswer("");
    } catch (e) {}
  };

  useEffect(() => {
    if (isSignUp) refreshCaptcha();
  }, [isSignUp]);

  const handleForgotPassword = async () => {
    if (!email) {
      setMessage(t("Enter your email address"));
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API_URL}/request-password-reset`, { email });
      setMessage(response.data.message);
      setIsPasswordReset(true);
      setIsForgotPassword(false);
    } catch (error) {
      setMessage(error.response?.data?.detail || "Failed to request password reset");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!verificationCode || !newPassword) {
      setMessage(t("Enter the code and new password"));
      return;
    }
    if (!resetPasswordStrength.isValid) {
      setMessage(t("Password does not meet security requirements"));
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API_URL}/reset-password`, {
        email,
        code: verificationCode,
        new_password: newPassword,
      });
      setMessage(t("Password changed successfully! You can now sign in with your new password."));
      setIsPasswordReset(false);
      setVerificationCode("");
      setNewPassword("");
      setEmail("");
      setResetPasswordStrength({ score: 0, message: "" });
    } catch (error) {
      if (error.response?.status === 422) {
        const errorData = error.response.data;
        if (errorData.error?.details) {
          const validationErrors = errorData.error.details.map((err) => err.msg).join(", ");
          setMessage(`Validation error: ${validationErrors}`);
        } else {
          setMessage(t("Validation error"));
        }
      } else if (error.response?.status === 400) {
        setMessage(error.response.data?.detail || "Invalid or expired password reset code");
      } else {
        setMessage(error.response?.data?.detail || "Password reset error");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <Link to="/" className="back-to-landing">
        ← {t("Back to Home")}
      </Link>

      <h1 className="auth-title">Supchats</h1>

      {!isPasswordReset && (
        <div className="auth-tabs">
          <button
            className={!isSignUp && !isForgotPassword ? "active" : ""}
            onClick={() => {
              setIsSignUp(false);
              setIsForgotPassword(false);
            }}
          >
            {t("Sign In")}
          </button>
          <button
            className={isSignUp ? "active" : ""}
            onClick={() => {
              setIsSignUp(true);
              setIsForgotPassword(false);
            }}
          >
            {t("Sign Up")}
          </button>
        </div>
      )}

      {isPasswordReset && (
        <div className="auth-header">
          <h2>{t("Password Reset")}</h2>
          <p>{t("Enter the code from email and new password")}</p>
        </div>
      )}

      <form className="auth-form" onSubmit={handleFormSubmit}>
        {isPasswordReset && (
          <>
            <input type="hidden" name="username" value={email} autoComplete="username" />
            <input
              type="text"
              name="verification-code"
              placeholder={t("Verification Code (6 digits)")}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              className="form-input"
              maxLength="6"
            />
            <input
              type="password"
              name="new-password"
              placeholder={t("New Password")}
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setResetPasswordStrength(checkPasswordStrength(e.target.value));
              }}
              className="form-input"
              autoComplete="new-password"
            />
            {newPassword && resetPasswordStrength.requirements && (
              <div className="password-requirements">
                <div className="requirements-title">{t("Password Requirements:")}</div>
                {Object.entries(resetPasswordStrength.requirements).map(([key, req]) => (
                  <div key={key} className={`requirement-item ${req.met ? "met" : "unmet"}`}>
                    <span className="requirement-icon">{req.met ? "✓" : "✗"}</span>
                    <span className="requirement-text">{req.message}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {!isPasswordReset && (
          <>
            <label className="form-label">{t("Email *")}</label>
            <input
              type="email"
              name="username"
              placeholder={t("Email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="form-input"
              autoComplete="username"
            />

            {!isForgotPassword && (
              <>
                <label className="form-label">{t("Password *")}</label>
                <input
                  type="password"
                  name="password"
                  placeholder={t("Password")}
                  value={password}
                  onChange={handlePasswordChange}
                  className="form-input"
                  autoComplete={isSignUp ? "new-password" : "current-password"}
                />
              </>
            )}
          </>
        )}

        {!isPasswordReset && isSignUp && (
          <>
            <label className="form-label">{t("Confirm Password *")}</label>
            <input
              type="password"
              name="confirm-password"
              placeholder={t("Confirm Password")}
              value={repeatPassword}
              onChange={(e) => setRepeatPassword(e.target.value)}
              className="form-input"
              autoComplete="new-password"
            />
            {passwordStrength.message && (
              <div
                className={`password-strength ${
                  passwordStrength.score < 3 ? "weak" : passwordStrength.score < 5 ? "medium" : "strong"
                }`}
              >
                {passwordStrength.message}
              </div>
            )}
            {password && passwordStrength.requirements && (
              <div className="password-requirements">
                <div className="requirements-title">{t("Password Requirements:")}</div>
                {Object.entries(passwordStrength.requirements).map(([key, req]) => (
                  <div key={key} className={`requirement-item ${req.met ? "met" : "unmet"}`}>
                    <span className="requirement-icon">{req.met ? "✓" : "✗"}</span>
                    <span className="requirement-text">{req.message}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="form-field-with-tooltip">
              <label className="form-label">
                {t("Contact Information *")}
                <div className="tooltip-container">
                  <span className="tooltip-icon">?</span>
                  <div className="tooltip-content">
                    {t("Please provide real contact details (email, phone, Telegram, etc.)")}
                  </div>
                </div>
              </label>
              <textarea
                placeholder={t("Enter your contact details (email, phone, Telegram, etc.)")}
                value={contactDetails}
                onChange={(e) => setContactDetails(e.target.value)}
                className="form-textarea"
                required
              />
            </div>
            <div className="captcha-block">
              <label className="form-label">{t("Captcha *")}</label>
              <div className="captcha-row">
                {captchaImage ? (
                  <img src={captchaImage} alt="captcha" className="captcha-image" />
                ) : (
                  <div className="captcha-placeholder">{t("Loading captcha...")}</div>
                )}
                <button
                  type="button"
                  className="captcha-refresh"
                  onClick={refreshCaptcha}
                  title={t("Refresh captcha")}
                >
                  ↻
                </button>
              </div>
              <input
                type="text"
                placeholder={t("Enter the text from the image")}
                value={captchaAnswer}
                onChange={(e) => setCaptchaAnswer(e.target.value)}
                className="form-input"
              />
            </div>
            <div className="checkbox-label">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="form-checkbox"
              />
              <span>
                {t("I accept the ")}
                <a href="/privacy" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                  {t("Privacy Policy")}
                </a>
              </span>
            </div>
          </>
        )}

        {!isPasswordReset && !isSignUp && !isForgotPassword && (
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="form-checkbox"
            />
            <span>{t("Remember me")}</span>
          </label>
        )}

        {!isPasswordReset && !isSignUp && !isForgotPassword && (
          <button
            type="button"
            onClick={() => setIsForgotPassword(true)}
            className="forgot-password-link"
          >
            {t("Forgot Password?")}
          </button>
        )}

        <button type="submit" className="submit-button" disabled={isLoading}>
          {isLoading
            ? t("Loading...")
            : isPasswordReset
              ? t("Reset Password")
              : isSignUp
                ? t("Sign Up")
                : isForgotPassword
                  ? t("Send")
                  : t("Sign In")}
        </button>

        {shouldShowGoogleButton && (
          <div className="auth-google-block">
            <div className="auth-google-divider">{t("Or continue with Google")}</div>
            <div ref={googleButtonContainerRef} className="auth-google-button-container" />
          </div>
        )}

        {(isForgotPassword || isPasswordReset) && (
          <button
            type="button"
            onClick={() => {
              setIsForgotPassword(false);
              setIsPasswordReset(false);
              setNewPassword("");
              setMessage("");
            }}
            className="back-to-login-link"
          >
            {t("Back to Sign In")}
          </button>
        )}

        {message && (
          <p
            className={`message ${
              message.toLowerCase().includes("success") ||
              message.toLowerCase().includes("successfully") ||
              message.toLowerCase().includes("logged in")
                ? "success"
                : message.toLowerCase().includes("verify") ||
                    message.toLowerCase().includes("verification") ||
                    message.toLowerCase().includes("check your email") ||
                    message.toLowerCase().includes("sent to your email")
                  ? "info"
                  : "error"
            }`}
          >
            {message}
          </p>
        )}

        {showPostRegisterModal && (
          <div className="modal-overlay" onClick={closePostRegisterModal} style={{ zIndex: 2000 }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{t("Thanks for signing up!")}</h3>
                <button className="modal-close" onClick={closePostRegisterModal}>
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p>{t("Please check your email inbox and spam folder for the verification email.")}</p>
                <p>
                  {t(
                    "Click the verification link to activate your account. After that, sign in below."
                  )}
                </p>
                <div className="post-register-actions">
                  <a
                    href={getWebmailUrl(email) || `mailto:${email || ""}`}
                    target={getWebmailUrl(email) ? "_blank" : undefined}
                    rel={getWebmailUrl(email) ? "noopener noreferrer" : undefined}
                    className="post-register-email-btn"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t("Open email client")}
                  </a>
                  <button
                    className="submit-button post-register-continue-btn"
                    onClick={closePostRegisterModal}
                  >
                    {t("Continue")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
